import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Heart, MessageCircle, Trash2, Zap, MoreHorizontal, Flag, VolumeX, X } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatDistanceToNow } from 'date-fns';
import ReportModal from './ReportModal';

function getInitials(name) {
  return (name || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
}

// Module-level set so render stays pure (react-hooks/immutability) but we
// still de-dupe schema-mismatch reports per vibe id.
const _vibePostReportedMissingText = new Set();

export default function VibePost({ vibe, onDelete, onUpdate }) {
  const { user: currentUser } = useAuth();
  const navigate = useNavigate();
  const [liked, setLiked] = useState(vibe.is_liked);
  const [likesCount, setLikesCount] = useState(vibe.likes_count || 0);
  const [showComments, setShowComments] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsCount, setCommentsCount] = useState(vibe.comments_count || 0);
  const [newComment, setNewComment] = useState('');
  const [loadingComments, setLoadingComments] = useState(false);
  const [menuFor, setMenuFor] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [reportUser, setReportUser] = useState(null);
  const [vibeMenuOpen, setVibeMenuOpen] = useState(false);

  const isVibeOwner = currentUser?.id === vibe.user?.id;

  const timeAgo = vibe.created_at
    ? formatDistanceToNow(new Date(vibe.created_at), { addSuffix: true })
    : '';

  const handleLike = async () => {
    try {
      if (liked) {
        await api.delete(`/vibes/${vibe.id}/like`);
        setLiked(false);
        setLikesCount(c => c - 1);
      } else {
        await api.post(`/vibes/${vibe.id}/like`);
        setLiked(true);
        setLikesCount(c => c + 1);
      }
    } catch {}
  };

  const handleToggleComments = async () => {
    if (!showComments) {
      setLoadingComments(true);
      try {
        const { data } = await api.get(`/vibes/${vibe.id}/comments`);
        setComments(data);
      } catch {}
      setLoadingComments(false);
    }
    setShowComments(!showComments);
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    try {
      const { data } = await api.post(`/vibes/${vibe.id}/comments`, { text: newComment });
      setComments([...comments, data]);
      setCommentsCount(c => c + 1);
      setNewComment('');
      if (onUpdate) onUpdate();
    } catch {}
  };

  const handleDeleteVibe = async () => {
    try {
      await api.delete(`/vibes/${vibe.id}`);
      if (onDelete) onDelete(vibe.id);
    } catch {}
  };

  const handleDeleteComment = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/vibes/comments/${confirmDelete.id}`);
      setComments(prev => prev.filter(c => c.id !== confirmDelete.id));
      setCommentsCount(c => Math.max(0, c - 1));
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to delete');
    }
    setConfirmDelete(null);
    setMenuFor(null);
  };

  const handleMuteUser = async (userId) => {
    try { await api.post(`/users/${userId}/mute`); } catch {}
    setMenuFor(null);
  };

  const renderText = (text) => {
    if (!text) {
      // Resilient: render nothing but report the data-quality issue to the
      // admin schema counters so we can spot underlying inconsistencies.
      if (vibe?.id && !_vibePostReportedMissingText.has(vibe.id)) {
        _vibePostReportedMissingText.add(vibe.id);
        api.post('/admin/schema/frontend', {
          component: 'VibePost',
          field: 'text',
          doc_id: vibe.id || '',
          note: 'rendered with missing text',
        }).catch(() => { /* fail-soft */ });
      }
      return null;
    }
    return text.split(/(#\w+)/g).map((part, i) => {
      if (part.startsWith('#')) {
        const tag = part.slice(1).toLowerCase();
        return (
          <span
            key={i}
            className="hashtag"
            onClick={(e) => { e.stopPropagation(); navigate(`/hashtag/${tag}`); }}
          >
            {part}
          </span>
        );
      }
      return part;
    });
  };

  return (
    <div data-testid={`vibe-${vibe.id}`} className="bg-[#FFFFFF] rounded-2xl border border-black/[0.06] p-4 animate-fade-in">
      <div className="flex items-start gap-3">
        <div
          className="flex-shrink-0 cursor-pointer"
          onClick={() => navigate(`/user/${vibe.user?.id}`)}
        >
          {vibe.user?.profile_photo ? (
            <img loading="lazy" decoding="async" src={vibe.user.profile_photo} alt="" className="w-10 h-10 rounded-full object-cover" />
          ) : (
            <div className="w-10 h-10 rounded-full bg-[#F1F0EA] flex items-center justify-center text-xs font-bold text-[#9B9EA3]">
              {getInitials(vibe.user?.display_name)}
            </div>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span
              className="font-semibold text-sm text-[#111111] cursor-pointer hover:underline"
              onClick={() => navigate(`/user/${vibe.user?.id}`)}
            >
              {vibe.user?.display_name}
            </span>
            <span className="text-[11px] text-[#9B9EA3]">@{vibe.user?.username}</span>
            <span className="text-[11px] text-[#777]">{timeAgo}</span>
            {!isVibeOwner && (
              <button
                data-testid={`vibe-menu-${vibe.id}`}
                onClick={(e) => { e.stopPropagation(); setVibeMenuOpen(true); }}
                className="ml-auto -mr-1 p-1 text-[#9B9EA3] hover:text-[#111111] transition-colors"
                aria-label="Vibe options"
              >
                <MoreHorizontal size={16} />
              </button>
            )}
          </div>
          <p
            className="text-sm text-[#2A2A2A] mt-1.5 leading-relaxed whitespace-pre-wrap cursor-pointer"
            onClick={() => navigate(`/vibe/${vibe.id}`)}
          >
            {renderText(vibe.text)}
          </p>
          {vibe.image && (
            <img loading="lazy" decoding="async"
              src={vibe.image}
              alt=""
              className="mt-3 rounded-xl w-full max-h-[300px] object-cover cursor-pointer"
              onClick={() => navigate(`/vibe/${vibe.id}`)}
            />
          )}
          <div className="flex items-center gap-5 mt-3">
            <button
              data-testid={`like-btn-${vibe.id}`}
              onClick={handleLike}
              className="flex items-center gap-1.5 text-xs transition-colors active:scale-95"
            >
              <Heart
                size={18}
                className={liked ? 'fill-[#FF3B30] text-[#FF3B30]' : 'text-[#9B9EA3]'}
              />
              <span className={liked ? 'text-[#FF3B30]' : 'text-[#9B9EA3]'}>{likesCount}</span>
            </button>
            <button
              data-testid={`comment-btn-${vibe.id}`}
              onClick={handleToggleComments}
              className="flex items-center gap-1.5 text-xs text-[#9B9EA3] transition-colors active:scale-95"
            >
              <MessageCircle size={18} />
              <span>{commentsCount}</span>
            </button>
            <button
              data-testid={`create-plan-from-vibe-${vibe.id}`}
              onClick={(e) => { e.stopPropagation(); navigate(`/create?from_vibe=${encodeURIComponent(vibe.text.slice(0, 60))}`); }}
              className="flex items-center gap-1 text-[11px] text-[#6B8A00] font-medium px-2 py-0.5 rounded-full border border-[#6B8A00]/20 hover:bg-[#6B8A00]/10 transition-all active:scale-95"
              title="Create plan from this vibe"
            >
              <Zap size={12} /> Plan
            </button>
            {isVibeOwner && (
              <button
                data-testid={`delete-vibe-${vibe.id}`}
                onClick={handleDeleteVibe}
                className="ml-auto text-[#9B9EA3] hover:text-[#FF3B30] transition-colors active:scale-95"
              >
                <Trash2 size={16} />
              </button>
            )}
          </div>

          {showComments && (
            <div className="mt-3 space-y-2.5">
              {loadingComments ? (
                <div className="skeleton h-8 w-full" />
              ) : (
                comments.map(c => {
                  const isAuthor = currentUser?.id === c.user?.id;
                  const canDelete = isAuthor || isVibeOwner;
                  return (
                    <div key={c.id} data-testid={`inline-comment-${c.id}`} className="flex items-start gap-2 text-xs group">
                      {c.user?.profile_photo ? (
                        <img loading="lazy" decoding="async"
                          src={c.user.profile_photo}
                          alt=""
                          className="w-6 h-6 rounded-full object-cover flex-shrink-0 cursor-pointer"
                          onClick={() => c.user?.id && navigate(`/user/${c.user.id}`)}
                        />
                      ) : (
                        <div
                          className="w-6 h-6 rounded-full bg-[#F1F0EA] flex items-center justify-center text-[8px] font-bold text-[#9B9EA3] flex-shrink-0 cursor-pointer"
                          onClick={() => c.user?.id && navigate(`/user/${c.user.id}`)}
                        >
                          {getInitials(c.user?.display_name)}
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <span className="font-semibold text-[#111111]">{c.user?.display_name}</span>{' '}
                        <span className="text-[#6B6E73]">{c.text}</span>
                      </div>
                      <button
                        data-testid={`inline-comment-menu-${c.id}`}
                        onClick={() => setMenuFor({ ...c, _canDelete: canDelete })}
                        className="flex-shrink-0 text-[#B0B3B8] hover:text-[#111111] transition-colors"
                        aria-label="Comment options"
                      >
                        <MoreHorizontal size={14} />
                      </button>
                    </div>
                  );
                })
              )}
              <form onSubmit={handleAddComment} className="flex gap-2 mt-2">
                <input
                  data-testid={`comment-input-${vibe.id}`}
                  type="text"
                  value={newComment}
                  onChange={e => setNewComment(e.target.value)}
                  placeholder="Add a comment..."
                  className="flex-1 bg-[#F1F0EA] text-[#111111] text-xs rounded-full px-3 py-2 border border-black/[0.06] focus:outline-none focus:border-[#B6E600]/30 placeholder-[#B0B3B8]"
                />
                <button
                  type="submit"
                  data-testid={`submit-comment-${vibe.id}`}
                  className="text-[#B6E600] text-xs font-semibold px-3 active:scale-95 transition-transform"
                >
                  Post
                </button>
              </form>
              <button
                onClick={() => navigate(`/vibe/${vibe.id}`)}
                className="text-[11px] text-[#9B9EA3] font-medium"
                data-testid={`view-vibe-${vibe.id}`}
              >
                View full vibe →
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Comment Actions Sheet */}
      {menuFor && (
        <div
          className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/30 animate-fade-in"
          onClick={() => setMenuFor(null)}
          data-testid="inline-comment-menu-overlay"
        >
          <div
            className="w-full max-w-[480px] bg-white rounded-t-3xl pb-safe-area animate-slide-up"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-[#E5E4DE] rounded-full" />
            </div>
            <div className="px-5 pt-3 pb-1">
              <p className="text-sm font-bold text-[#111111]">@{menuFor.user?.username}</p>
            </div>
            <div className="px-3 pt-2">
              {menuFor._canDelete && (
                <button
                  data-testid="inline-comment-delete-btn"
                  onClick={() => setConfirmDelete(menuFor)}
                  className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left"
                >
                  <div className="w-10 h-10 rounded-2xl bg-[#FF3B30]/10 flex items-center justify-center flex-shrink-0">
                    <Trash2 size={18} className="text-[#FF3B30]" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-[#FF3B30]">Delete Comment</p>
                    <p className="text-xs text-[#9B9EA3] mt-0.5">Remove this comment permanently</p>
                  </div>
                </button>
              )}
              {currentUser?.id !== menuFor.user?.id && (
                <>
                  <button
                    data-testid="inline-comment-mute-btn"
                    onClick={() => menuFor.user?.id && handleMuteUser(menuFor.user.id)}
                    className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-2xl bg-[#F1F0EA] flex items-center justify-center flex-shrink-0">
                      <VolumeX size={18} className="text-[#111111]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-[#111111]">Mute User</p>
                      <p className="text-xs text-[#9B9EA3] mt-0.5">Hide their vibes and plans from your feed</p>
                    </div>
                  </button>
                  <button
                    data-testid="inline-comment-report-btn"
                    onClick={() => { setMenuFor(null); setReportUser(menuFor.user); }}
                    className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left"
                  >
                    <div className="w-10 h-10 rounded-2xl bg-[#FF3B30]/10 flex items-center justify-center flex-shrink-0">
                      <Flag size={18} className="text-[#FF3B30]" />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-[#FF3B30]">Report User</p>
                      <p className="text-xs text-[#9B9EA3] mt-0.5">Flag behavior that breaks our guidelines</p>
                    </div>
                  </button>
                </>
              )}
            </div>
            <div className="px-5 pt-4">
              <button
                onClick={() => setMenuFor(null)}
                className="w-full py-3 rounded-full bg-[#F1F0EA] text-sm font-medium text-[#111111]"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete */}
      {confirmDelete && (
        <div
          className="fixed inset-0 z-[10010] flex items-center justify-center bg-black/40 px-6 animate-fade-in"
          onClick={() => setConfirmDelete(null)}
        >
          <div
            className="w-full max-w-[360px] bg-white rounded-3xl p-5 animate-slide-up"
            onClick={e => e.stopPropagation()}
            data-testid="inline-comment-delete-confirm"
          >
            <div className="flex items-start justify-between mb-2">
              <h3 className="text-base font-bold text-[#111111]">Delete comment?</h3>
              <button onClick={() => setConfirmDelete(null)} className="text-[#9B9EA3] -mt-1 -mr-1">
                <X size={18} />
              </button>
            </div>
            <p className="text-sm text-[#6B6E73] leading-relaxed">This action cannot be undone.</p>
            <div className="mt-5 grid grid-cols-2 gap-2">
              <button
                onClick={() => setConfirmDelete(null)}
                className="py-3 rounded-full bg-[#F1F0EA] text-sm font-semibold text-[#111111]"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteComment}
                className="py-3 rounded-full bg-[#FF3B30] text-sm font-bold text-white"
                data-testid="inline-comment-delete-confirm-btn"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {reportUser && (
        <ReportModal type="user" targetId={reportUser.id} onClose={() => setReportUser(null)} />
      )}

      {/* Vibe-level Actions Sheet (Report / Mute author) */}
      {vibeMenuOpen && vibe.user && (
        <div
          className="fixed inset-0 z-[10000] flex items-end justify-center bg-black/30 animate-fade-in"
          onClick={() => setVibeMenuOpen(false)}
          data-testid={`vibe-menu-sheet-${vibe.id}`}
        >
          <div
            className="w-full max-w-[480px] bg-white rounded-t-3xl pb-safe-area animate-slide-up"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 bg-[#E5E4DE] rounded-full" />
            </div>
            <div className="px-5 pt-3 pb-1">
              <p className="text-sm font-bold text-[#111111]">@{vibe.user.username}</p>
            </div>
            <div className="px-3 pt-2">
              <button
                data-testid={`vibe-mute-author-${vibe.id}`}
                onClick={() => { setVibeMenuOpen(false); handleMuteUser(vibe.user.id); }}
                className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-2xl bg-[#F1F0EA] flex items-center justify-center flex-shrink-0">
                  <VolumeX size={18} className="text-[#111111]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#111111]">Mute User</p>
                  <p className="text-xs text-[#9B9EA3] mt-0.5">Hide their vibes and plans from your feed</p>
                </div>
              </button>
              <button
                data-testid={`vibe-report-author-${vibe.id}`}
                onClick={() => { setVibeMenuOpen(false); setReportUser(vibe.user); }}
                className="w-full flex items-center gap-4 p-3 rounded-2xl active:bg-[#F1F0EA] transition-colors text-left"
              >
                <div className="w-10 h-10 rounded-2xl bg-[#FF3B30]/10 flex items-center justify-center flex-shrink-0">
                  <Flag size={18} className="text-[#FF3B30]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold text-[#FF3B30]">Report Vibe</p>
                  <p className="text-xs text-[#9B9EA3] mt-0.5">Flag content that breaks our guidelines</p>
                </div>
              </button>
            </div>
            <div className="px-5 pt-4">
              <button
                onClick={() => setVibeMenuOpen(false)}
                className="w-full py-3 rounded-full bg-[#F1F0EA] text-sm font-medium text-[#111111]"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
